import React, { useState, useCallback } from 'react';
import {
  Button,
  Spinner,
  Select,
} from 'ui-component-library/components';
import {
  string,
  bool,
  func,
  number,
} from 'prop-types';
import * as yup from 'yup';

import { logger } from '../../../../../utils/common';
import allContactsByCustomerBk from '../../../../../utils/api/address-book';
import { createGroupedOptions } from '../../../../../utils/common/createGroupedOptions';
import { formatErrorResponse } from '../../../../../utils/mappers';

import {
  StyledEmailModal,
  Container,
  TextFieldContainer,
  TextFieldHeaderDiv,
  CustomTextFieldLabel,
  SubjectText,
  TextAreaContainer,
  EmailBody,
  ButtonWrapper,
  SpinnerContainer,
  DisabledEmailField,
  ContentContainer,
} from './email-modal.style';
import emailForm from '../../../../../shapes/email-form';

export const schema = yup.array().of(
  yup.object().shape({
    label: yup.string().email().required(),
  }),
);

/**
 * Creates select input option from custom input
 * @param {string} value - Custom input value
 * @returns {object}
 */
const customInputConversion = (value) => (
  {
    value,
    label: value,
    name: value,
  }
);

/**
 * Email Modal Component
 * @param {object} params - object containing props
 * @param {object} params.emailContent - Object that contains forms initial values
 * @param {func} params.setEmailContent - function to update emailContent
 * @param {boolean} params.showModal - initial value to show or not show the modal
 * @param {func} params.closeModal - function to handle modal close
 * @param {func} params.onEmailSubmit - function to handle submit button
 * @param {boolean} params.isLoading - boolean for is loading
 * @param {string} - Title of the email
 * @param {number} params.textAreaheight - Height of the text area
 * @param {string} params.cancelButtonLabel - Text to display in the cancel button
 * @param {string} params.customerBk - CustomerBK for a given load
 * @return {element}
 */
const EmailModal = (
  {
    showModal,
    closeModal,
    emailContent,
    onEmailSubmit,
    isLoading,
    title,
    textAreaHeight,
    cancelButtonLabel,
    customerBk,
  },
) => {
  const [emailModalContent, setEmailModalContent] = useState(emailContent);
  const [selectedToEmails,
    setSelectedToEmails] = useState(emailContent.recipient ? [customInputConversion(emailContent.recipient)] : []);
  const [hideSelectResults, setHideSelectResults] = useState(true);
  const [recipientInputValue, setRecipientInputValue] = useState();
  const [validationErrors, setValidationErrors] = useState({});

  /**
   * Validates the select input has valid emails
   * @param {string} value - array of emails
   * @returns {element}
   */
  const validateEmails = (value) => {
    const errors = { ...validationErrors };
    const email = value?.map((item) => item.label).join(';');
    setEmailModalContent({ ...emailContent, recipient: email });
    if (value?.length > 0) {
      try {
        schema.validateSync(value, { abortEarly: false });
        errors.recipient = null;
      } catch (err) {
        errors.recipient = 'Invalid email recipient';
      }
    } else {
      errors.recipient = 'Please enter an email recipient';
    }
    setValidationErrors(errors);
  };

  /**
   * Search handler for searching contacts in select
   * @param {string} searchParams - search parameter to match contacts
   * @returns {object}
   */
  const onSearchEmails = useCallback(async (searchParams) => {
    try {
      const result = await allContactsByCustomerBk({
        customerBk,
        searchTerm: searchParams,
      });
      return createGroupedOptions(
        result.data.model.outlookContacts,
        result.data.model.addressContacts,
        result.data.model.recentContacts,
      );
    } catch (error) {
      logger.error(formatErrorResponse(error));
    }
    return [];
  }, [customerBk]);

  /**
   * Prevents duplicates in select input
   * @param {array} emails - email list
   * @returns {array}
   */
  const deleteDuplicates = (emails) => {
    const setObj = new Set();
    const result = emails.reduce((acc, email) => {
      if (!setObj.has(email.label)) {
        setObj.add(email.label, email);
        acc.push(email);
      }
      return acc;
    }, []);
    return result;
  };

  /**
   * Handles when value is selected or created
   * @param {object or string} value - Custom input value or selected value
   * @returns {void}
   */
  const handleSelectOnChange = (value) => {
    let email;
    if (typeof value === 'string') {
      // Email was added from the select (single option returned)
      email = [
        ...selectedToEmails,
        customInputConversion(value),
      ];
    } else {
      // Email was deleted from the select (new array or null returned)
      email = value ?? [];
    }
    email = deleteDuplicates(email);
    setSelectedToEmails(email);
    validateEmails(email);
  };

  /**
   * Handles when typed input value is changed
   * @param {string} value - Typed input value
   * @returns {void}
   */
  const onInputValueChange = (value) => {
    let email = value;
    setHideSelectResults(!(value?.length > 2));
    if (value?.length > 1 && value[value?.length - 1] === ';') {
      setHideSelectResults(true);
      handleSelectOnChange(value.substring(0, value.length - 1));
      email = '';
    }
    setRecipientInputValue(email);
  };

  return (
    <div data-testid="email-modal">
      <StyledEmailModal
        title={title}
        isOpen={showModal}
        onModalClose={closeModal}
        headerContent={<div />}
        width="480px"
      >
        <ContentContainer>
          {isLoading && (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          )}
          {!isLoading && (
            <Container>
              <TextFieldContainer>
                <TextFieldHeaderDiv>
                  <CustomTextFieldLabel htmlFor="recipient">*To</CustomTextFieldLabel>
                </TextFieldHeaderDiv>
                <Select
                  selectedValue={selectedToEmails}
                  optionLoader={onSearchEmails}
                  onValueChange={(value) => handleSelectOnChange(value)}
                  isMulti
                  isEmail
                  validation={validationErrors.recipient ? 'failed' : undefined}
                  validationMessage={validationErrors.recipient}
                  suppressMenu={hideSelectResults}
                  setSuppressMenu={(value) => setHideSelectResults(value)}
                  inputValue={recipientInputValue}
                  onInputValueChange={(value) => onInputValueChange(value)}
                  icon="Search"
                  placeholder="Add recipient(s) - separate recipients by semicolon (;)"
                />
              </TextFieldContainer>
              <TextFieldContainer>
                <TextFieldHeaderDiv>
                  <CustomTextFieldLabel htmlFor="copyTo">Copy To</CustomTextFieldLabel>
                </TextFieldHeaderDiv>
                <DisabledEmailField>
                  {emailModalContent.copyTo}
                </DisabledEmailField>
              </TextFieldContainer>
              <TextFieldContainer>
                <TextFieldHeaderDiv>
                  <CustomTextFieldLabel htmlFor="subject">Subject Line</CustomTextFieldLabel>
                </TextFieldHeaderDiv>
                <SubjectText>
                  {emailModalContent.subject}
                </SubjectText>
              </TextFieldContainer>
              <TextAreaContainer>
                <TextFieldHeaderDiv>
                  <CustomTextFieldLabel htmlFor="body">Message</CustomTextFieldLabel>
                </TextFieldHeaderDiv>
                <EmailBody textAreaHeight={textAreaHeight}>
                  {emailModalContent.body}
                </EmailBody>
              </TextAreaContainer>
              <ButtonWrapper>
                <Button
                  text={cancelButtonLabel}
                  isSecondary
                  size="large"
                  onClick={closeModal}
                />
                <Button
                  text="Send"
                  size="large"
                  isDisabled={validationErrors.recipient}
                  onClick={() => onEmailSubmit(emailModalContent)}
                />
              </ButtonWrapper>
            </Container>
          )}
        </ContentContainer>
      </StyledEmailModal>
    </div>
  );
};

EmailModal.propTypes = {
  showModal: bool,
  closeModal: func,
  emailContent: emailForm,
  onEmailSubmit: func,
  isLoading: bool,
  title: string,
  textAreaHeight: string,
  cancelButtonLabel: string,
  customerBk: number.isRequired,
};

EmailModal.defaultProps = {
  showModal: false,
  closeModal: () => { },
  emailContent: {},
  onEmailSubmit: () => { },
  isLoading: false,
  title: 'Email Modal',
  textAreaHeight: '400px',
  cancelButtonLabel: 'Cancel',
};

export default EmailModal;
